import { useEffect, useRef, useState } from "react";
import secureLocalStorage from "react-secure-storage";
import { downloadCustomersToExcel, searchCustomers } from "../../services/api/customers/CustomersService";
import { useNavigate } from "react-router-dom";
import { ICustomer } from "../../@types/customer.type";
import { FeatureStatic } from "../../@types/statics/feature.static";
import addCircleImage from "./../../assets/_AddCircleIcon_.svg";
import iconExcel from "./../../assets/icon-excel.svg";
import { Button } from "../../components/Button";
import usePermission from "../../hooks/usePermission";
import { getAllCustomersCustomField } from "../../services/api/customerCustomField/customerCustomFieldService";
import { ICustomField } from "../../@types/customField.type";

import { AgGridReact } from "ag-grid-react";
import { ColDef, FilterChangedEvent, IGetRowsParams } from "ag-grid-community";

import { themeGrid } from "../../themeGrid";
import { localeText } from "../../localeTextFromGrid";
import styles from "./styles.module.scss";
import { IFilterDownload, IFilterRequest } from "../../@types/filter-request.type";

const columnsDefault: ColDef[] = [
  {
    field: "id",
    headerName: "ID",
    hide: true,
  },
  {
    field: "name",
    headerName: "Nome",
    flex: 1,
    minWidth: 200,
  },
  {
    field: "description",
    headerName: "Descrição",
    flex: 1,
    minWidth: 200,
  },
  {
    field: "originId",
    headerName: "Id Origem",
    flex: 1,
    minWidth: 200,
  },
];

export const Customers: React.FC = () => {
  const [columns, setColumns] = useState(columnsDefault);
  const [loading, setLoading] = useState<boolean>(false);
  const gridRef = useRef<AgGridReact<any>>(null);
  
  let filters = {} as IFilterDownload;
  const navigate = useNavigate();
  const permissions = usePermission(FeatureStatic.clients.id);

  const getFromCache = (key: string) => {
    const data = secureLocalStorage.getItem(key);
    return data ? JSON.parse(data as string) : null;
  };

  const [cachedFilters, setCachedFilters] = useState(
    getFromCache("agGridFiltersCustomers")
  );
  const [cachedSort, setCachedSort] = useState(
    getFromCache("agGridSortCustomers")
  );

  const saveToCache = (key: string, data: any) => {
    secureLocalStorage.setItem(key, JSON.stringify(data));
  };

  const onFilterChanged = (params: FilterChangedEvent) => {
    const filters = params.api.getFilterModel(); // Obtém o modelo de filtro
    saveToCache("agGridFiltersCustomers", filters); // Salva no cache
  };

  const onSortChanged = () => {
    const colState = gridRef.current!.api.getColumnState();
    const sortState = colState
      .filter(function (s) {
        return s.sort != null;
      })
      .map(function (s) {
        return { colId: s.colId, sort: s.sort, sortIndex: s.sortIndex };
      });

    saveToCache("agGridSortCustomers", sortState);
  };

  useEffect(() => {
    if (!gridRef.current) return;

    const gridApi = gridRef.current.api;

    if (gridApi) {
      // Restaurar filtros
      if (cachedFilters) {
        gridApi.setFilterModel(cachedFilters);
      }

      // Restaurar ordenação
      if (cachedSort) {
        gridApi.applyColumnState({
          state: cachedSort,
          defaultState: { sort: null },
        });
      }
    }
  }, [cachedFilters, cachedSort, columns]);

  function mapCustomFieldsToTheCollumnGrid(
    customFields: ICustomField[]
  ): ColDef[] {
    return customFields
      ?.filter((item) => item.showInList)
      .map((item) => ({
        id: item.id,
        field: item.name,
        headerName: item.name,
        flex: 1,
        minWidth: 200,
        filter: false,
        sortable: false,
      }));
  }

  function mapCustomFieldsToTheRowGrid(customers: ICustomer[]): ICustomer[] {
    return customers.map((customer) => {
      const customFieldsObject = Array.isArray(customer.customFields)
        ? Object.fromEntries(
            customer.customFields.map(({ key, value }) => [key, value])
          )
        : {};

      return {
        ...customer,
        ...customFieldsObject,
        customFields: undefined,
      };
    });
  }

  async function fetchAllCustomersCustomField() {
    try {
      setLoading(true);

      const responseCollumms = await getAllCustomersCustomField();

      const mappedCustomColumns = mapCustomFieldsToTheCollumnGrid(
        responseCollumms.result.items
      );

      setColumns([...columns, ...mappedCustomColumns]);
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (!permissions.canView()) {
      navigate("/unauthorized");
      return;
    }

    fetchAllCustomersCustomField();
  }, []);

  function navigateToUpdatePage(id: string) {
    navigate(`update/${id}`);
  }

  const getDataSource = () => ({
    getRows: async (params: IGetRowsParams) => {
      console.log(params);

      const sortModel = params.sortModel;
      const filterModel = params.filterModel;

      const filter = {
        skip: params.startRow, // Linha inicial para paginação
        take: params.endRow - params.startRow, // Quantidade de linhas
      } as IFilterRequest;

      if (sortModel.length > 0) {
        filter.orderByField = sortModel[0].colId.replace(
          /(\w)(\w*)/g,
          function (g0, g1, g2) {
            return g1.toUpperCase() + g2.toLowerCase();
          }
        );
        filter.orderByDirection = sortModel[0].sort == "asc" ? 0 : 1;
      }

      if (filterModel.name) {
        filters.name = filterModel.name.filter;
        filter.name = filterModel.name.filter;
      }else{
        filters.name = undefined;
      }


      if (filterModel.description) {
        filters.description = filterModel.description.filter;
        filter.description = filterModel.description.filter;
      }else{
        filters.description = undefined;
      }

      if (filterModel.originId) {
        filters.originId = filterModel.originId.filter;
        filter.originId = filterModel.originId.filter;
      }else{
        filters.originId = undefined;
      }


      try {
        const response = await searchCustomers(filter);

        const customerWithCustomizedFields = mapCustomFieldsToTheRowGrid(
          response.result.items
        );

        params.successCallback(
          customerWithCustomizedFields,
          response.result.totalRows
        );
      } catch (error) {
        console.error("Erro ao buscar dados:", error);
        params.failCallback(); // Notifica falha no carregamento
      }
    },
  });

  async function ExportToExcel(filters: IFilterDownload) {
    await downloadCustomersToExcel(filters);
  }

  return (
    <>
      <main
        className={`${styles.allCustomers} ${
          permissions.canEdit() && styles.isEdit
        }`}
      >
        <div className={styles.titleAndBtnCreate}>
          <h1>Clientes</h1>
          <div className={styles.btnsFilterAndCreate}>
            {permissions.canEdit() && (
              <Button
                iconUrl={addCircleImage}
                onClick={() => {
                  navigate("/customers/create");
                }}
                content="Novo Cliente"
                className={styles.button}
              />
            )}
          </div>
        </div>
        <div className={styles.divExcel}>
          <Button
            onClick={() => {
              ExportToExcel(filters);
            }}
            iconUrl={iconExcel}
            className={styles.btnExcel}
          />
        </div>
        <div className={`${styles.dataGridContainer}`}>
          <AgGridReact
            ref={gridRef}
            theme={themeGrid}
            localeText={localeText}
            loading={loading}
            columnDefs={columns}
            overlayNoRowsTemplate="<span style='color: gray;'>Sem registros encontrados</span>"
            defaultColDef={{
              sortable: true,
              editable: false,
              filter: true,
              floatingFilter: true,
            }}
            suppressCellFocus={true}
            rowStyle={{ cursor: "pointer" }}
            onRowClicked={(row) =>
              permissions.canEdit()
                ? navigateToUpdatePage(row.data.id)
                : undefined
            }
            cacheBlockSize={10}
            onFilterChanged={onFilterChanged}
            onSortChanged={onSortChanged}
            rowModelType="infinite"
            datasource={getDataSource()}
            animateRows={true}
          />
        </div>
      </main>
    </>
  );
};
